/**
 * Calcul la position de l'élément par rapport au haut de la page
 * @param {HTMLElement} element
 * @param {number} acc
 * @return {number}
 */
function offsetTop(element, acc = 0) {
  if (element.offsetParent) {
    return offsetTop(element.offsetParent, acc + element.offsetTop);
  }
  return acc + element.offsetTop;
}

/**
 * @property {HTMLElement} element
 * @property {{y: number, r: number, variable: boolean}} options
 */
class Parallax {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    this.options = this.parseAttribute();

    this.onScroll = this.onScroll.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.onResize = this.onResize.bind(this);

    this.elementY = offsetTop(this.element) + this.element.offsetHeight / 2;
    const observer = new IntersectionObserver(this.onIntersection);
    observer.observe(element);
    this.onScroll();
  }

  /**
   * @returns {Parallax[]}
   */
  static bind() {
    return Array.from(document.querySelectorAll("[data-parallax]")).map(
      (element) => {
        return new Parallax(element);
      }
    );
  }

  parseAttribute() {
    const defaultOptions = {
      y: 0.2,
      r: 0,
      variable: false,
    };
    if (this.element.dataset.parallax.startsWith("{")) {
      return {
        ...defaultOptions,
        ...JSON.parse(this.element.dataset.parallax),
      };
    }
    return { ...defaultOptions, y: parseFloat(this.element.dataset.parallax) };
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  onIntersection(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting && !this.active) {
        this.active = true;
        document.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onResize);
        this.onResize();
      } else if (!entry.isIntersecting && this.active) {
        this.active = false;
        document.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("resize", this.onResize);
      }
    }
  }
  onResize() {
    this.elementY = offsetTop(this.element) + this.element.offsetHeight / 2;
    this.onScroll();
  }

  onScroll() {
    window.requestAnimationFrame(() => {
      const rect = this.element.getBoundingClientRect();

      const elementCenter = rect.top + rect.height / 2;

      const viewportCenter = window.innerHeight / 2;

      let t = (elementCenter - viewportCenter) / viewportCenter;

      t = Math.max(-1, Math.min(1, t));

      const amplitude = this.options.max ?? 120;
      const translateY = -t * amplitude * (this.options.y ?? 1);

      if (this.options.variable) {
        this.element.style.setProperty("--parallaxY", `${translateY}px`);
      } else {
        let transform = `translateY(${translateY}px)`;
        if (this.options.r) {
          transform += ` rotate(${t * (this.options.r * 10)}deg)`;
        }
        this.element.style.setProperty("transform", transform);
      }
    });
  }}

export default Parallax;